/**
 * Data layer for `affiliate-referral-program`. Thin typed fetch wrappers over
 * /api/v1/affiliate-referral-program. Raw `fetch()` is confined to this file
 * per the dash contract.
 */

export type ReferralStatus = "pending" | "active" | "revoked";

export interface Tier {
	level: number;
	bonus_bps: number;
}

export interface ProgramView {
	enabled: boolean;
	tiers: Tier[];
	max_depth: number;
	attribution_window: number;
	terms_url: string;
	updated_at: number;
}

export interface ReferralView {
	id: string;
	referrer_id: string;
	referred_id: string;
	depth: number;
	status: ReferralStatus;
	code: string;
	created_at: number;
	activated_at: number | null;
}

export interface ListReferralsResponse {
	count: number;
	referrals: ReferralView[];
}

export interface LedgerView {
	id: string;
	ancestor_id: string;
	descendant_id: string;
	depth: number;
	bonus_bps: number;
	base_payout_cents: number;
	bonus_cents: number;
	source_ref: string;
	created_at: number;
}

export interface ListLedgerResponse {
	count: number;
	limit: number;
	ledger: LedgerView[];
}

export interface SummaryView {
	affiliate_id: string;
	direct_referrals: number;
	by_status: Record<ReferralStatus, number>;
	bonus_rows: number;
	total_bonus_cents: number;
	by_depth: Record<number, { bonus_cents: number; rows: number }>;
}

export interface UpsertProgramBody {
	enabled?: boolean;
	tiers?: Tier[];
	max_depth?: number;
	attribution_window?: number;
	terms_url?: string;
}

export interface CreateReferralBody {
	referrer_id: string;
	referred_id: string;
	depth?: number;
	code?: string;
	status?: ReferralStatus;
}

export interface UpdateReferralBody {
	status?: ReferralStatus;
	code?: string;
}

export interface AccrueBody {
	descendant_id: string;
	base_payout_cents: number;
	source_ref?: string;
}

export interface AccrueResponse {
	descendant_id: string;
	accrued: LedgerView[];
	total_bonus_cents: number;
	reason?: string;
}

const BASE = "/api/v1/affiliate-referral-program";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function getProgram(): Promise<ProgramView> {
	const res = await fetch(`${BASE}/program`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ProgramView>(res);
}

export async function upsertProgram(body: UpsertProgramBody): Promise<ProgramView> {
	const res = await fetch(`${BASE}/program`, {
		method: "PUT",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<ProgramView>(res);
}

export async function listReferrals(params?: {
	referrer_id?: string;
	referred_id?: string;
	status?: ReferralStatus;
}): Promise<ListReferralsResponse> {
	const qs = new URLSearchParams();
	if (params?.referrer_id) qs.set("referrer_id", params.referrer_id);
	if (params?.referred_id) qs.set("referred_id", params.referred_id);
	if (params?.status) qs.set("status", params.status);
	const suffix = qs.toString() ? `?${qs.toString()}` : "";
	const res = await fetch(`${BASE}/referrals${suffix}`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListReferralsResponse>(res);
}

export async function createReferral(body: CreateReferralBody): Promise<ReferralView> {
	const res = await fetch(`${BASE}/referrals`, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<ReferralView>(res);
}

export async function updateReferral(
	id: string,
	body: UpdateReferralBody,
): Promise<ReferralView> {
	const res = await fetch(`${BASE}/referrals/${encodeURIComponent(id)}`, {
		method: "PUT",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<ReferralView>(res);
}

export async function listLedger(params?: {
	ancestor_id?: string;
	from?: number;
	to?: number;
	limit?: number;
}): Promise<ListLedgerResponse> {
	const qs = new URLSearchParams();
	if (params?.ancestor_id) qs.set("ancestor_id", params.ancestor_id);
	if (params?.from !== undefined) qs.set("from", String(params.from));
	if (params?.to !== undefined) qs.set("to", String(params.to));
	if (params?.limit !== undefined) qs.set("limit", String(params.limit));
	const suffix = qs.toString() ? `?${qs.toString()}` : "";
	const res = await fetch(`${BASE}/ledger${suffix}`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListLedgerResponse>(res);
}

export async function getSummary(affiliateId: string): Promise<SummaryView> {
	const res = await fetch(
		`${BASE}/summary/${encodeURIComponent(affiliateId)}`,
		{
			method: "GET",
			credentials: "include",
			headers: { accept: "application/json" },
		},
	);
	return parseOrThrow<SummaryView>(res);
}

export async function accrue(body: AccrueBody): Promise<AccrueResponse> {
	const res = await fetch(`${BASE}/accrue`, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<AccrueResponse>(res);
}

export const affiliateReferralProgramQueryKeys = {
	all: ["affiliate-referral-program"] as const,
	program: () => [...affiliateReferralProgramQueryKeys.all, "program"] as const,
	referrals: (filters?: {
		referrer_id?: string;
		referred_id?: string;
		status?: ReferralStatus;
	}) =>
		[
			...affiliateReferralProgramQueryKeys.all,
			"referrals",
			filters ?? {},
		] as const,
	ledger: (filters?: { ancestor_id?: string; limit?: number }) =>
		[
			...affiliateReferralProgramQueryKeys.all,
			"ledger",
			filters ?? {},
		] as const,
	summary: (affiliateId: string) =>
		[
			...affiliateReferralProgramQueryKeys.all,
			"summary",
			affiliateId,
		] as const,
};
